import { Trans, useLingui } from '@lingui/react'
import { shape, string } from 'prop-types'
import { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11'
import { useDispatch, useSelector } from 'react-redux'

import { CSRF_FIELD_NAME } from 'shared/consts/httpHeaders'
import {
  addAddressAction,
  changeAddressAction,
  getCustomerSalutationAndName,
  hasCartRxEntries,
} from 'shared/store-checkout/duck/address'
import { hideModalAction } from 'shared/store/ducks/events'
import Button from 'views/components/atoms/Button'
import Form from 'views/components/atoms/Form'
import InputSuggest from 'views/components/molecules/InputSuggest'
import InputTextWithWithFloatingLabel from 'views/components/molecules/InputTextWithFloatingLabel'
import ModalContext from 'views/components/molecules/Modal/ModalContext'
import Notification from 'views/components/molecules/Notification'
import AddressFirstLastName from 'views/components/organisms/AddressFirstLastName'
import AddressFormContext, {
  emtpySubmitAction,
} from 'views/components/organisms/AddressForm/AddressFormContext'
import Salutation from 'views/components/organisms/Salutation'
import { DUMMY_SELLER_ID } from 'views/consts'
import {
  ADDRESS_SALUTATION,
  BILLING,
  BILLING_FORM_ID,
  SHIPPING,
} from 'views/consts/address'
import { SALUTATION_MR, SALUTATION_MRS } from 'views/consts/customer'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import buildClientAPI from 'views/providers/clientAPI'
import {
  cityValidator,
  phoneValidator,
  salutationValidator,
  streetNumberValidator,
  streetValidator,
  zipValidator,
} from 'views/utils/formValidationHelper'

import './AddressForm.scss'

const zipFetchLength = 3
const zipCompleteLength = 5
const streetFetchLength = 1

const AddressForm = ({
  addressId,
  type,
  initialFormValues,
  modalId,
  csrfToken,
  publicRuntimeConfig: {
    publicConfig: { addressSuggestCountryCode, language, validation },
  },
}) => {
  const { i18n } = useLingui()
  const dispatch = useDispatch()
  const cartHasRXEntries = useSelector(state => hasCartRxEntries(state))
  const customerSalutationAndName = useSelector(state =>
    getCustomerSalutationAndName(state)
  )

  const isEmptyForm =
    !initialFormValues.plz &&
    !initialFormValues.city &&
    !initialFormValues.street

  const { salutation, firstName, lastName } =
    type === BILLING ? customerSalutationAndName : initialFormValues

  const { register, handleSubmit, errors, reset, watch, setValue } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: initialFormValues,
    submitFocusError: true,
  })

  const modalRef = useContext(ModalContext)
  const {
    postBillingSubmitAction,
    setPostBillingSubmitAction,
    postShippingSubmitAction,
    setPostShippingSubmitAction,
  } = useContext(AddressFormContext)

  const handleReset = () => {
    reset({ ...initialFormValues })
  }

  const allowAddressSuggest = addressSuggestCountryCode === language

  const [initialFetchDone, setInitialFetchDone] = useState(false)
  const [asid, setAsid] = useState(null)
  const [zips, setZips] = useState(null)
  const [streets, setStreets] = useState(null)

  const fetchZips = async zipPrefix => {
    try {
      const { addressSuggestService } = buildClientAPI()
      const response = await addressSuggestService.fetchZips(asid, zipPrefix)
      setZips(response.data.zips)
    } catch (e) {
      // use to logger in future
      // console.log('Cannot fetch zips: ', e)
    }
  }

  const fetchCities = async zip => {
    try {
      const { addressSuggestService } = buildClientAPI()
      const response = await addressSuggestService.fetchCity(asid, zip)
      setValue('city', response.data.cities[0])
    } catch (e) {
      // use to logger in future
      // console.log('Cannot fetch city: ', e)
    }
  }

  const fetchStreets = async (zip, prefix) => {
    try {
      const { addressSuggestService } = buildClientAPI()
      const response = await addressSuggestService.fetchStreets({
        asid,
        zip,
        prefix,
      })
      setStreets(response.data.streets)
    } catch (e) {
      // use to logger in future
      // console.log('Cannot fetch streets: ', e)
    }
  }

  const onZipChange = e => {
    const input = e.target.value
    if (input.length === zipFetchLength) {
      fetchZips(input)
    }
    if (input.length === zipCompleteLength) {
      fetchCities(input)
      if (watch('street').length) {
        // if we already have input in the street we "pre"-fetch the streets
        fetchStreets(watch('zip'), watch('street')[0])
      }
    }
    if (input.length !== zipCompleteLength && streets?.length) {
      // reset suggest streets to [] if no valid zip is provided
      setStreets([])
    }
  }

  const initialFetch = () => {
    const zipValue = watch('zip')
    const streetValue = watch('street')
    if (zipValue.length === zipCompleteLength) {
      fetchZips(zipValue)
      if (streetValue.length) {
        fetchStreets(zipValue, streetValue[0])
      }
    }
    setInitialFetchDone(true)
  }

  // TODO for code owners: consider fixing and/or removing this line
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    let registerInterval
    if (allowAddressSuggest) {
      const { addressSuggestService } = buildClientAPI()
      const registerAddressSuggest = async () => {
        try {
          const response = await addressSuggestService.register()
          const id = response.data.success.asid
          setAsid(id)
        } catch (e) {
          // use to logger in future
          // console.log('Cannot register to AddressSuggest: ', e)
        }
      }
      registerAddressSuggest()
      const threeMinutesInMs = 3 * 60 * 1000
      registerInterval = setInterval(() => {
        registerAddressSuggest()
      }, threeMinutesInMs)
    }
    return () => {
      if (registerInterval) {
        clearInterval(registerInterval)
      }
    }
  }, [])

  useEffect(() => {
    if (asid && !initialFetchDone) {
      initialFetch()
    }
  }, [asid])

  const onStreetChange = e => {
    const input = e.target.value
    if (
      input.length === streetFetchLength &&
      watch('zip').length === zipCompleteLength
    ) {
      fetchStreets(watch('zip'), input)
    }
  }

  useEffect(() => {
    if (modalRef) {
      modalRef.on('hide', handleReset)
    }
  }, [modalRef])

  useEffect(() => {
    handleReset()
  }, [initialFormValues])
  /* eslint-enable react-hooks/exhaustive-deps */

  const onSubmit = formData => {
    const { billingAddressNames, ...formDataCleaned } = formData
    if (modalId) {
      dispatch(hideModalAction(modalId))
    }

    if (type === BILLING) {
      if (isEmptyForm) {
        dispatch(
          addAddressAction({
            ...formDataCleaned,
            type,
            salutation,
            firstName,
            lastName,
            default: true,
          })
        )
      } else {
        dispatch(
          changeAddressAction(addressId, {
            salutation: ADDRESS_SALUTATION[salutation],
            firstName,
            lastName,
            ...formDataCleaned,
            type,
          })
        )
      }
      postBillingSubmitAction()
      setPostBillingSubmitAction(emtpySubmitAction)
    } else if (type === SHIPPING) {
      if (isEmptyForm) {
        dispatch(addAddressAction({ ...formDataCleaned, type, default: true }))
      } else {
        dispatch(
          changeAddressAction(addressId, {
            ...formDataCleaned,
            type,
          })
        )
      }
      postShippingSubmitAction()
      setPostShippingSubmitAction(emtpySubmitAction)
    }
  }

  const salutationLabel = {
    [SALUTATION_MR]: i18n._('general.label.salutation.mr'),
    [SALUTATION_MRS]: i18n._('general.label.salutation.mrs'),
    'mr': i18n._('general.label.salutation.mr'),
    'mrs': i18n._('general.label.salutation.mrs'),
  }[salutation]

  const formId =
    type === BILLING
      ? `${BILLING_FORM_ID}`
      : `${type}-address-form-${addressId}`

  const customerWantsToChangeExistingBillingAddress =
    modalRef && type === BILLING && !isEmptyForm

  return (
    <Form
      name={formId}
      id={formId}
      method="POST"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* use "|| undefined" to prevent warning if csrfToken is null
          as uncontrolled <input> param "value" must not be null
          but instead should be undefined
         */}
      <input type="hidden" name={CSRF_FIELD_NAME} value={csrfToken} />
      <input type="hidden" name="type" value={type} />
      {customerWantsToChangeExistingBillingAddress && (
        <div className="u-margin-large--bottom">
          <Notification
            label={i18n._('addressForm.notification.changeBilling')}
            type="info"
          />
        </div>
      )}
      {type === SHIPPING && (
        <>
          <Salutation
            name="salutation"
            value={salutation}
            type={`${type}-${addressId}`}
            required
            register={register(salutationValidator)}
            errors={errors}
          />
          <AddressFirstLastName
            firstName={firstName}
            lastName={lastName}
            type={`${type}-${addressId}`}
            register={register}
            errors={errors}
            watch={watch}
          />
        </>
      )}
      <ul className="l-grid l-grid--tight">
        {type === BILLING && (
          <li className="l-grid__item u-margin--bottom">
            <InputTextWithWithFloatingLabel
              name="billingAddressNames"
              id={`${type}-billingAddressNames-${addressId}`}
              inputType="text"
              value={`${salutationLabel} ${firstName} ${lastName}`}
              tooltipText={i18n._('addressForm.tooltip.name')}
              required
              disabled
              register={register}
              errors={errors}
              useExternalValidator
              isDeclarative
            >
              <Trans id="general.label.billingAddressNames" />
            </InputTextWithWithFloatingLabel>
          </li>
        )}
        <li className="l-grid__item l-col--4-of-12 l-col--3-of-12@large u-margin--bottom">
          <InputSuggest
            name="zip"
            id={`${type}-zip-${addressId}`}
            inputType="text"
            required
            register={register(zipValidator(validation.address.zipCode))}
            errors={errors}
            useExternalValidator
            watch={watch('zip')}
            onChange={allowAddressSuggest && onZipChange}
            options={zips}
          >
            <Trans id="general.label.zip" />
          </InputSuggest>
        </li>
        <li className="l-grid__item l-col--8-of-12 l-col--9-of-12@large u-margin--bottom">
          <InputSuggest
            name="city"
            id={`${type}-city-${addressId}`}
            inputTFype="text"
            required
            register={register(cityValidator)}
            errors={errors}
            useExternalValidator
            watch={watch('city')}
          >
            <Trans id="general.label.city" />
          </InputSuggest>
        </li>
        <li className="l-grid__item l-col--8-of-12 l-col--9-of-12@large u-margin--bottom">
          <InputSuggest
            name="street"
            id={`${type}-street-${addressId}`}
            inputType="text"
            required
            register={register(streetValidator)}
            errors={errors}
            useExternalValidator
            watch={watch('street')}
            onChange={allowAddressSuggest && onStreetChange}
            options={streets}
          >
            <Trans id="general.label.street" />
          </InputSuggest>
        </li>
        <li className="l-grid__item l-col--4-of-12 l-col--3-of-12@large u-margin--bottom">
          <InputTextWithWithFloatingLabel
            name="streetNo"
            id={`${type}-streetNo-${addressId}`}
            inputType="text"
            required
            register={register(
              streetNumberValidator(validation.address.streetNumber)
            )}
            errors={errors}
            useExternalValidator
            watch={watch('streetNo')}
          >
            <Trans id="general.label.streetNo" />
          </InputTextWithWithFloatingLabel>
        </li>
        <li className="l-grid__item u-margin--bottom">
          <InputTextWithWithFloatingLabel
            name="extension"
            id={`${type}-extension-${addressId}`}
            inputType="text"
            register={register}
            errors={errors}
            useExternalValidator
            watch={watch('extension')}
          >
            <Trans id="addressForm.label.extensionOptional" />
          </InputTextWithWithFloatingLabel>
        </li>
        {type === BILLING && (
          <li className="l-grid__item u-margin--bottom">
            <InputTextWithWithFloatingLabel
              name="phone"
              id={`${type}-phone-${addressId}`}
              inputType="tel"
              tooltipText={i18n._('addressForm.tooltip.phone')}
              required={cartHasRXEntries}
              register={cartHasRXEntries ? register(phoneValidator) : register}
              errors={errors}
              useExternalValidator
              watch={watch('phone')}
              isDeclarative
            >
              {cartHasRXEntries ? (
                <Trans id="general.form.label.phone" />
              ) : (
                <Trans id="addressForm.label.phoneNumberOptional" />
              )}
            </InputTextWithWithFloatingLabel>
          </li>
        )}
        <li className="l-grid__item u-no-margin--bottom u-margin-x-small--top">
          <h4 className="u-font-weight--normal font-size-base-small u-color--grey u-no-margin u-padding-large--bottom">
            <Trans id="addressForm.deliveryRestrictions.text" />
          </h4>
        </li>
      </ul>
      {modalId && (
        <ul className="o-AddressForm__form_navigation a-list-reset l-flex l-flex--distribute-space@medium l-flex__column_reverse@small-tiny l-flex--vertical-stretch@small-tiny">
          <li className="o-AddressForm__form_navigation__col-button">
            <Button
              style="buttonGhost"
              data-a11y-dialog-hide
              name="AddressForm-back"
              isClientsideControlled
              type="button"
              onClick={handleReset}
            >
              <Trans id="general.link.label.back" />
            </Button>
          </li>
          <li className="o-AddressForm__form_navigation__col-button u-margin-small--bottom@small-tiny u-margin-small--left@medium">
            <Button name="AddressForm-submit" type="submit" form={formId}>
              {isEmptyForm && type === SHIPPING && (
                <Trans id="modal.address.navigation.addShippingAddress" />
              )}
              {isEmptyForm && type === BILLING && (
                <Trans id="modal.address.navigation.addBillingAddress" />
              )}
              {!isEmptyForm && (
                <Trans id="modal.address.navigation.acceptChanges" />
              )}
            </Button>
          </li>
        </ul>
      )}
    </Form>
  )
}

AddressForm.propTypes = {
  addressId: string,
  type: string.isRequired,
  initialFormValues: shape({}),
  i18n: shape({}).isRequired,
  modalId: string,
  csrfToken: string,
  publicRuntimeConfig: shape({}),
}

AddressForm.defaultProps = {
  addressId: DUMMY_SELLER_ID,
  initialFormValues: {},
  modalId: '',
  csrfToken: undefined,
  publicRuntimeConfig: {},
}

export default withShopAndRuntimeConfig(AddressForm)
